import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';

import { AuthContext } from '../../context/AuthProvider';

import clientsServices from '../../services/clientsServices';
import userServices from '../../services/userServices';
import scheduledAppointmentsServices from '../../services/scheduledAppointmentsServices';

const ADMIN_ROLES = ['superadmin', 'admin', 'dispatcher'];
const SALESMAN_ROLES = ['technician', 'dispatcher', 'admin', 'superadmin'];

const OFFICE_PHONE = '(619) 655-3010';

// ===== Default notification templates (placeholders resolved in the form) =====

const formatDateTime = (value) => {
  if (!value) return { dateStr: '', timeStr: '' };
  const d = new Date(value);
  if (isNaN(d.getTime())) return { dateStr: '', timeStr: '' };
  const dateStr = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return { dateStr, timeStr };
};

export const buildClientMessage = ({ clientName, salesmanName, scheduledFor }) => {
  const { dateStr, timeStr } = formatDateTime(scheduledFor);
  const when = dateStr ? ` on ${dateStr}${timeStr ? ` around ${timeStr}` : ''}` : '';
  return (
    `Hi ${clientName || 'there'},\n\n` +
    `This is a friendly reminder from AirMaxx Air Conditioning & Heating. ` +
    `${salesmanName || 'Your AirMaxx representative'} will be giving you a call${when} ` +
    `to follow up on your HVAC project.\n\n` +
    `If this time no longer works for you, please call our office at ${OFFICE_PHONE} and we will reschedule.\n\n` +
    `Thank you for choosing AirMaxx!\n\n` +
    `Best regards,\nAirMaxx Team`
  );
};

export const buildSalesmanMessage = ({ clientName, clientPhone, scheduledFor, note }) => {
  const { dateStr, timeStr } = formatDateTime(scheduledFor);
  const when = dateStr ? ` scheduled for ${dateStr}${timeStr ? ` at ${timeStr}` : ''}` : '';
  return (
    `AirMaxx CRM reminder: follow-up call with ${clientName || 'client'}` +
    `${clientPhone ? ` (${clientPhone})` : ''}${when}.` +
    `${note ? `\n\nNotes: ${note}` : ''}`
  );
};

// Convert a Date/ISO string to the value format of <input type="datetime-local">
const toLocalInputValue = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultScheduledFor = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalInputValue(d);
};

const clientLabel = (client) => {
  if (!client) return '';
  const name = client.fullName || `${client.firstName || ''} ${client.lastName || ''}`.trim();
  return `${name}${client.phoneNumber ? ` — ${client.phoneNumber}` : ''}`;
};

/**
 * Create/edit modal for a deferred call (Scheduled Appointment).
 *
 * Props:
 * - open, onClose, onSaved
 * - initialClient / initialSalesman / appointmentId: prefill when opened from an appointment
 * - editItem: existing scheduled appointment for edit mode
 */
const ScheduledAppointmentModal = ({
  open,
  onClose,
  onSaved,
  initialClient = null,
  initialSalesman = null,
  appointmentId = null,
  editItem = null,
}) => {
  const { userData } = useContext(AuthContext);
  const isAdmin = ADMIN_ROLES.includes(userData?.role);

  const [client, setClient] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [salesmanId, setSalesmanId] = useState('');
  const [users, setUsers] = useState([]);
  const [scheduledFor, setScheduledFor] = useState(defaultScheduledFor());
  const [note, setNote] = useState('');
  const [notifyClientSms, setNotifyClientSms] = useState(true);
  const [notifyClientEmail, setNotifyClientEmail] = useState(false);
  const [notifySalesmanSms, setNotifySalesmanSms] = useState(true);
  const [notifySalesmanEmail, setNotifySalesmanEmail] = useState(false);
  const [clientMessage, setClientMessage] = useState('');
  const [salesmanMessage, setSalesmanMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // Track manual edits so we stop regenerating the template under the user's hands
  const clientMsgTouched = useRef(false);
  const salesmanMsgTouched = useRef(false);
  const searchTimer = useRef(null);

  const salesman = useMemo(() => {
    if (isAdmin) return users.find((u) => u.id === salesmanId) || null;
    return userData || null;
  }, [isAdmin, users, salesmanId, userData]);

  // Initialize form when the modal opens
  useEffect(() => {
    if (!open) return;

    clientMsgTouched.current = !!editItem;
    salesmanMsgTouched.current = !!editItem;

    if (editItem) {
      setClient(editItem.client || null);
      setSalesmanId(editItem.salesman?.id || '');
      setScheduledFor(toLocalInputValue(editItem.scheduledFor));
      setNote(editItem.note || '');
      setNotifyClientSms(!!editItem.notifyClientSms);
      setNotifyClientEmail(!!editItem.notifyClientEmail);
      setNotifySalesmanSms(!!editItem.notifySalesmanSms);
      setNotifySalesmanEmail(!!editItem.notifySalesmanEmail);
      setClientMessage(editItem.clientMessage || '');
      setSalesmanMessage(editItem.salesmanMessage || '');
    } else {
      setClient(initialClient || null);
      setSalesmanId(initialSalesman?.id || userData?.id || '');
      setScheduledFor(defaultScheduledFor());
      setNote('');
      setNotifyClientSms(true);
      setNotifyClientEmail(false);
      setNotifySalesmanSms(true);
      setNotifySalesmanEmail(false);
      setClientMessage('');
      setSalesmanMessage('');
    }

    // Seed options so the preselected value is present in the Autocomplete list
    if (editItem?.client) setClientOptions([editItem.client]);
    else if (initialClient) setClientOptions([initialClient]);
  }, [open, editItem, initialClient, initialSalesman, userData]);

  // Load users for the salesman dropdown (admins only)
  useEffect(() => {
    if (!open || !isAdmin) return;
    userServices
      .getAssignableUsers(false)
      .then((response) => {
        // /user endpoint returns { data: [...], total }
        const raw = Array.isArray(response.data) ? response.data : response.data?.data;
        const list = (Array.isArray(raw) ? raw : []).filter((u) => SALESMAN_ROLES.includes(u.role) && !u.deletedAt);
        setUsers(list);
      })
      .catch((error) => console.error('Failed to load users:', error));
  }, [open, isAdmin]);

  // Regenerate default templates while the user has not edited them manually
  useEffect(() => {
    if (!open) return;
    const clientName = client?.firstName || client?.fullName || '';
    const salesmanName = salesman?.fullName || '';

    if (!clientMsgTouched.current) {
      setClientMessage(buildClientMessage({ clientName, salesmanName, scheduledFor }));
    }
    if (!salesmanMsgTouched.current) {
      setSalesmanMessage(
        buildSalesmanMessage({
          clientName: client?.fullName || clientName,
          clientPhone: client?.phoneNumber,
          scheduledFor,
          note,
        }),
      );
    }
  }, [open, client, salesman, scheduledFor, note]);

  const searchClients = (query) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!query || query.length < 2) return;

    searchTimer.current = setTimeout(async () => {
      setClientSearchLoading(true);
      try {
        const response = await clientsServices.searchRequest(0, 20, query);
        // /client endpoint returns { data: [...], total }
        const list = Array.isArray(response.data) ? response.data : response.data?.data;
        setClientOptions(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Client search failed:', error);
      } finally {
        setClientSearchLoading(false);
      }
    }, 350);
  };

  const resetClientMessage = () => {
    clientMsgTouched.current = false;
    setClientMessage(
      buildClientMessage({
        clientName: client?.firstName || client?.fullName || '',
        salesmanName: salesman?.fullName || '',
        scheduledFor,
      }),
    );
  };

  const resetSalesmanMessage = () => {
    salesmanMsgTouched.current = false;
    setSalesmanMessage(
      buildSalesmanMessage({
        clientName: client?.fullName || '',
        clientPhone: client?.phoneNumber,
        scheduledFor,
        note,
      }),
    );
  };

  const handleSave = async () => {
    if (!client?.id) {
      toast.warning('Please select a client');
      return;
    }
    if (!scheduledFor) {
      toast.warning('Please select date and time');
      return;
    }
    if (new Date(scheduledFor).getTime() <= Date.now() && !editItem) {
      toast.warning('Date and time must be in the future');
      return;
    }

    const payload = {
      clientId: client.id,
      salesmanId: isAdmin ? salesmanId || undefined : undefined,
      appointmentId: appointmentId || undefined,
      scheduledFor: new Date(scheduledFor).toISOString(),
      note: note || null,
      notifyClientSms: notifyClientSms && clientHasPhone,
      notifyClientEmail: notifyClientEmail && clientHasEmail,
      notifySalesmanSms: notifySalesmanSms && salesmanHasPhone,
      notifySalesmanEmail: notifySalesmanEmail && salesmanHasEmail,
      clientMessage,
      salesmanMessage,
    };

    setSaving(true);
    try {
      if (editItem) {
        await scheduledAppointmentsServices.update(editItem.id, payload);
        toast.success('Scheduled appointment updated');
      } else {
        await scheduledAppointmentsServices.create(payload);
        toast.success('Call has been added to the schedule');
      }
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to save scheduled appointment:', error);
      toast.error(error?.response?.data?.message || 'Failed to save scheduled appointment');
    } finally {
      setSaving(false);
    }
  };

  const clientHasPhone = !!client?.phoneNumber;
  const clientHasEmail = !!client?.email;
  const salesmanHasPhone = !!salesman?.phoneNumber;
  const salesmanHasEmail = !!salesman?.email;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneCallbackIcon color="primary" />
        {editItem ? 'Edit Scheduled Call' : 'Schedule a Follow-up Call'}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Client */}
          <Autocomplete
            value={client}
            options={clientOptions}
            getOptionLabel={clientLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(x) => x}
            loading={clientSearchLoading}
            onChange={(e, value) => setClient(value)}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') searchClients(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                placeholder="Search by name, phone or email..."
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clientSearchLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Salesman */}
          {isAdmin ? (
            <FormControl fullWidth>
              <InputLabel id="sched-salesman-label">Salesman</InputLabel>
              <Select
                labelId="sched-salesman-label"
                label="Salesman"
                value={users.some((u) => u.id === salesmanId) ? salesmanId : ''}
                onChange={(e) => setSalesmanId(e.target.value)}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField label="Salesman" value={userData?.fullName || ''} disabled fullWidth />
          )}

          {/* Date/time and note */}
          <TextField
            label="Call date & time"
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />

          <TextField
            label="Note (reason, context of the follow-up)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />

          <Divider />

          {/* Notifications */}
          <Typography variant="subtitle2">Notifications at the scheduled time</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Client{client ? '' : ' (select a client first)'}
              </Typography>
              <Box>
                <Tooltip title={clientHasPhone ? '' : 'Client has no phone number'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifyClientSms && clientHasPhone}
                        onChange={(e) => setNotifyClientSms(e.target.checked)}
                        disabled={!clientHasPhone}
                      />
                    }
                    label="SMS"
                  />
                </Tooltip>
                <Tooltip title={clientHasEmail ? '' : 'Client has no email'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifyClientEmail && clientHasEmail}
                        onChange={(e) => setNotifyClientEmail(e.target.checked)}
                        disabled={!clientHasEmail}
                      />
                    }
                    label="Email"
                  />
                </Tooltip>
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Salesman{isAdmin ? '' : ' (me)'}
              </Typography>
              <Box>
                <Tooltip title={salesmanHasPhone ? '' : 'Salesman has no phone number'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifySalesmanSms && salesmanHasPhone}
                        onChange={(e) => setNotifySalesmanSms(e.target.checked)}
                        disabled={!salesmanHasPhone}
                      />
                    }
                    label="SMS"
                  />
                </Tooltip>
                <Tooltip title={salesmanHasEmail ? '' : 'Salesman has no email'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifySalesmanEmail && salesmanHasEmail}
                        onChange={(e) => setNotifySalesmanEmail(e.target.checked)}
                        disabled={!salesmanHasEmail}
                      />
                    }
                    label="Email"
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Message templates */}
          {(notifyClientSms || notifyClientEmail) && (
            <TextField
              label="Message to client"
              value={clientMessage}
              onChange={(e) => {
                clientMsgTouched.current = true;
                setClientMessage(e.target.value);
              }}
              multiline
              minRows={4}
              fullWidth
              InputProps={{
                endAdornment: (
                  <Tooltip title="Reset to default template">
                    <IconButton size="small" onClick={resetClientMessage} sx={{ alignSelf: 'flex-start' }}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          )}

          {(notifySalesmanSms || notifySalesmanEmail) && (
            <TextField
              label="Message to salesman"
              value={salesmanMessage}
              onChange={(e) => {
                salesmanMsgTouched.current = true;
                setSalesmanMessage(e.target.value);
              }}
              multiline
              minRows={3}
              fullWidth
              InputProps={{
                endAdornment: (
                  <Tooltip title="Reset to default template">
                    <IconButton size="small" onClick={resetSalesmanMessage} sx={{ alignSelf: 'flex-start' }}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : editItem ? 'Save Changes' : 'Add to Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduledAppointmentModal;
